/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type ReactPortal,
} from 'react';

import { css, jsx } from '@compiled/react';
import { bindAll } from 'bind-event-listener';
import ReactDOM from 'react-dom';

import { getDocument } from '@atlaskit/browser-apis';
import {
	findClosestSnap,
	generateDefaultGuidelines,
	getGuidelineSnaps,
	getGuidelinesWithHighlights,
} from '@atlaskit/editor-common/guideline';
import { getParentNodeWidth } from '@atlaskit/editor-common/node-width';
import type { Dimensions, HandleResize, Position, Snap } from '@atlaskit/editor-common/resizer';
import { ResizerNext } from '@atlaskit/editor-common/resizer';
import type { EditorAppearance, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { akEditorFullWidthLayoutWidth } from '@atlaskit/editor-shared-styles';
import { getParameters } from '@atlaskit/native-embeds-common';
import type { AlignmentValue } from '@atlaskit/native-embeds-common';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';

import type { EditorPluginNativeEmbedsPlugin } from '../nativeEmbedsPluginType';
import { createUpdateAlignmentCommand } from '../pm-plugins/commands/alignment';
import { createUpdateWidthCommand, getWidth } from '../pm-plugins/commands/resize';
import { NATIVE_EMBED_PLUGIN_IS_RESIZING_KEY } from '../pm-plugins/main';
import { resolveLatestPosByLocalId } from '../pm-plugins/utils/resolveLatestPosByLocalId';
import { getRowBoundaries, subscribeRowBoundaries } from '../pm-plugins/utils/row-boundaries-store';
import { snapHeightToRowBoundary } from '../pm-plugins/utils/snap-to-row';

import {
	getNativeEmbedShouldLockAspectRatio,
	nativeEmbedAllowsVerticalResize,
} from './nativeEmbedManifest';
import { useNativeEmbedLoadingState } from './useNativeEmbedLoadingState';
import {
	calcMaxWidth,
	CSS_VAR_NATIVE_EMBED_WIDTH,
	mapAlignmentToLayout,
	MIN_HEIGHT,
	MIN_WIDTH,
	resizerNextTestId,
	SNAP_GAP,
} from './utils';

const resizerWrapperStyles = css({
	position: 'absolute',
	pointerEvents: 'auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'& > div': {
		position: 'absolute',
		pointerEvents: 'auto',
		maxWidth: 'var(--ak-editor-max-container-width)',
		minWidth: 'min(24px, 100%)',
	},
});

const resizerPlaceholderStyles = css({
	width: '100%',
	pointerEvents: 'none',
});

export type Target = {
	container: HTMLElement;
	innerContainer: HTMLElement | null;
	localId: string;
	node: PMNode;
	pos: number;
};

interface NodeRect {
	height: number;
	left: number;
	top: number;
	width: number;
}

type MeasuredRect = Pick<DOMRect, 'bottom' | 'height' | 'left' | 'right' | 'top' | 'width'>;

const getMeasuredResizeRect = (
	measureEl: HTMLElement,
	innerContainer?: HTMLElement | null,
): MeasuredRect => {
	const outerRect = measureEl.getBoundingClientRect();
	const innerRect = innerContainer?.getBoundingClientRect();
	// Wrapped embeds can collapse the node-view wrapper height while the iframe
	// remains visible, so fall back to the frame rect for overlay sizing.
	const visibleInnerRect =
		innerRect && innerRect.width > 0 && innerRect.height > 0
			? innerRect
			: measureEl.querySelector<HTMLElement>('iframe')?.getBoundingClientRect();

	if (!visibleInnerRect || visibleInnerRect.width <= 0 || visibleInnerRect.height <= 0) {
		return outerRect;
	}

	const left = Math.min(outerRect.left, visibleInnerRect.left);
	const right = Math.max(outerRect.right, visibleInnerRect.right);
	const top = Math.min(outerRect.top, visibleInnerRect.top);
	const bottom = Math.max(outerRect.bottom, visibleInnerRect.bottom);

	return {
		bottom,
		height: bottom - top,
		left,
		right,
		top,
		width: right - left,
	};
};

export type EmbedResizeOverlayProps = {
	appearance: EditorAppearance | undefined;
	containerWidth: number;
	currentAlignment: AlignmentValue;
	disabled: boolean;
	lineLength: number;
	mode: 'selected' | 'hover';
	onAlignmentChange?: (alignment: AlignmentValue) => void;
	/**
	 * Notify the coordinator that this overlay is starting a resize. The
	 * coordinator returns `false` if another overlay is already mid-resize and
	 * the start should be rejected; in that case the overlay should bail out of
	 * its own resize sequence. If the callback is not provided, the start is
	 * implicitly accepted.
	 */
	onResizeStart?: (localId: string) => boolean;
	/**
	 * Notify the coordinator that this overlay's resize sequence has finished.
	 * Always called with the same `localId` that was passed to `onResizeStart`.
	 */
	onResizeStop?: (localId: string) => void;
	pluginInjectionApi: ExtractInjectionAPI<EditorPluginNativeEmbedsPlugin> | undefined;
	portalTarget: HTMLElement;
	target: Target;
	view: EditorView;
};

export const EmbedResizeOverlay = (props: EmbedResizeOverlayProps): ReactPortal | null => {
	const {
		target,
		mode,
		view,
		portalTarget,
		pluginInjectionApi,
		appearance,
		disabled,
		containerWidth,
		lineLength,
		currentAlignment,
		onAlignmentChange,
		onResizeStart,
		onResizeStop,
	} = props;

	const disableHandles = !!disabled;
	const isLoadingGateEnabled = fg('platform_native_embeds_full_rollout_bugfix');
	const isNativeEmbedLoading = useNativeEmbedLoadingState(
		target.container,
		isLoadingGateEnabled,
		target.innerContainer,
	);
	const handlesBlocked = disableHandles || isNativeEmbedLoading;

	// -----------------------------------------------------------------------
	// Per-overlay state
	// -----------------------------------------------------------------------

	const [isResizing, setIsResizing] = useState(false);
	const [snaps, setSnaps] = useState<Snap>({});
	// Tracks which handle is currently being dragged so resizeRatio can be
	// switched per-direction (re-resizable applies a single ratio to both axes).
	const [activeResizeDirection, setActiveResizeDirection] = useState<
		'left' | 'right' | 'bottom' | null
	>(null);

	const isResizingRef = useRef(false);
	const aspectRatioRef = useRef<number | null>(null);
	const fixedHeightRef = useRef<number | null>(null);
	const lastSnappedGuidelineKeysRef = useRef<string[]>([]);
	const committedWidthRef = useRef<number | null>(null);
	const committedHeightRef = useRef<number | null>(null);
	const innerEmbedContainerRef = useRef<HTMLElement | null>(target.innerContainer);
	const resizeFrameRafRef = useRef<number | null>(null);
	const bottomDraggedRef = useRef(false);
	// For nested embeds, capture the actual pixel width of the parent container at
	// drag start. nestedMaxWidth is derived from ProseMirror schema attributes and may
	// not match the actual CSS-computed pixel width (e.g. layout columns with implicit
	// widths). This DOM measurement is the source of truth for clamping during drag.
	const nestedContainerWidthRef = useRef<number | null>(null);

	const liveRowBoundariesRef = useRef<number[] | undefined>(
		getRowBoundaries(target.localId),
	);
	const [embedDomElement, setEmbedDomElement] = useState<HTMLElement | null>(target.container);
	const [nodeRect, setNodeRect] = useState<NodeRect | null>(null);

	const localId = target.localId;
	useEffect(() => {

		liveRowBoundariesRef.current = getRowBoundaries(localId);

		return subscribeRowBoundaries(localId, (updated) => {
			liveRowBoundariesRef.current = updated;
		});
	}, [localId]);

	const updateMeasuredNodeRect = useCallback((measureEl: HTMLElement, container: HTMLElement) => {
		const containerRect = container.getBoundingClientRect();
		const nodeRectRaw = getMeasuredResizeRect(measureEl, innerEmbedContainerRef.current);
		const nextNodeRect = {
			top: nodeRectRaw.top - containerRect.top + container.scrollTop,
			left: nodeRectRaw.left - containerRect.left + container.scrollLeft,
			width: nodeRectRaw.width,
			height: nodeRectRaw.height,
		};

		setNodeRect((prevNodeRect) => {
			if (
				prevNodeRect &&
				prevNodeRect.top === nextNodeRect.top &&
				prevNodeRect.left === nextNodeRect.left &&
				prevNodeRect.width === nextNodeRect.width &&
				prevNodeRect.height === nextNodeRect.height
			) {
				return prevNodeRect;
			}

			return nextNodeRect;
		});

		if (
			committedHeightRef.current !== null &&
			Math.round(nextNodeRect.height) === committedHeightRef.current
		) {
			committedHeightRef.current = null;
		}
	}, []);

	// Sync refs / DOM element when the target changes
	useEffect(() => {
		setEmbedDomElement(target.container);
		innerEmbedContainerRef.current = target.innerContainer;
	}, [target]);

	// Stable ref to the localId we last reported as "started" so the unmount
	// cleanup can pair the start with a matching stop using the same id, even
	// if `target` has since changed identity.
	const resizingStartedLocalIdRef = useRef<string | null>(null);

	// Make sure if we unmount mid-drag the coordinator's resize ownership is
	// released. We pair start/stop by localId so the coordinator can ignore
	// stops for any embed other than the one currently owning the resize.
	useEffect(() => {
		return () => {
			if (isResizingRef.current && resizingStartedLocalIdRef.current) {
				const startedLocalId = resizingStartedLocalIdRef.current;
				isResizingRef.current = false;
				resizingStartedLocalIdRef.current = null;
				// Signal drag-stop to the editor-extension even if we unmount mid-drag.
				if (!getNativeEmbedShouldLockAspectRatio(target.node)) {
					try {
						target.container.dispatchEvent(
							new CustomEvent('native-embed-resize-state-change', {
								bubbles: true,
								detail: { active: false, localId: startedLocalId },
							}),
						);
					} catch {
						// best-effort signal
					}
				}
				onResizeStop?.(startedLocalId);
			}
			if (resizeFrameRafRef.current !== null) {
				cancelAnimationFrame(resizeFrameRafRef.current);
				resizeFrameRafRef.current = null;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// -----------------------------------------------------------------------
	// Width derivation
	// -----------------------------------------------------------------------

	const currentWidth = useMemo(() => {
		const node = target.node;
		if (!node) {
			return lineLength;
		}
		const storedWidth = getWidth(node);
		if (storedWidth) {
			return storedWidth;
		}

		const layout = node.attrs.layout || 'default';
		const paramWidth = node.attrs.parameters?.width;

		if (typeof paramWidth === 'number' && paramWidth > 0) {
			return paramWidth;
		}

		if (layout === 'wide') {
			return Math.ceil(lineLength * 1.4);
		}
		if (layout === 'full-width') {
			return akEditorFullWidthLayoutWidth;
		}
		return lineLength;
	}, [target.node, lineLength]);

	// Live-measured container width that overrides the potentially stale
	// plugin value. On non-maximized Mac windows the width plugin's
	// ResizeObserver can report an inflated containerWidth on initial page
	// load (before the content-tree sidebar has finished rendering). This
	// is updated at the start of each drag with the actual DOM measurement
	// and reset to null when the plugin value updates (meaning it caught up).
	const [liveMeasuredContainerWidth, setLiveMeasuredContainerWidth] = useState<number | null>(null);

	// When the plugin's containerWidth updates, clear the override — the
	// plugin has caught up (e.g. after a window resize).
	useEffect(() => {
		setLiveMeasuredContainerWidth(null);
	}, [containerWidth]);

	const effectiveContainerWidth = liveMeasuredContainerWidth ?? containerWidth;

	const nestedMaxWidth = useMemo(() => {
		if (!view) {
			return undefined;
		}
		try {
			return getParentNodeWidth(target.pos, view.state, {
				width: effectiveContainerWidth,
				lineLength,
			});
		} catch {
			return undefined;
		}
	}, [target.pos, view, effectiveContainerWidth, lineLength]);

	const isNested = nestedMaxWidth !== undefined;

	const maxWidth = useMemo(() => {
		return nestedMaxWidth ?? calcMaxWidth(effectiveContainerWidth, appearance);
	}, [nestedMaxWidth, effectiveContainerWidth, appearance]);

	const [resizeWidth, setResizeWidth] = useState<number>(currentWidth);
	const [resizeHeight, setResizeHeight] = useState<number | null>(null);

	useEffect(() => {
		if (!isResizing) {
			if (committedWidthRef.current !== null) {
				if (currentWidth === committedWidthRef.current) {
					committedWidthRef.current = null;
				}
				return;
			}
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- only one state update; the chain detection is a false positive when the initial useState value depends on a derived value (currentWidth)
			setResizeWidth(Math.min(currentWidth, maxWidth));
		}
	}, [currentWidth, isResizing, maxWidth]);

	const allowVerticalResize = useMemo(
		() => nativeEmbedAllowsVerticalResize(target.node),
		[target.node],
	);

	// For database native embeds with vertical resize (gated behind databases-native-embeds-v2),
	// add extra padding around the bottom drag handle.
	const bottomHandleStyles = useMemo(() => {
		if (!allowVerticalResize) {
			return undefined;
		}
		const handleHeight = token('space.150', '12px');
		return {
			bottom: {
				height: handleHeight,
				bottom: `calc(${handleHeight} * -1)`,
			},
		};
	}, [allowVerticalResize]);

	// -----------------------------------------------------------------------
	// Measure the embed DOM position for overlay positioning.
	// -----------------------------------------------------------------------
	useLayoutEffect(() => {
		if (isResizing) {
			return;
		}

		try {
			const measureEl = target.container;
			if (!measureEl) {
				setNodeRect(null);
				return;
			}
			updateMeasuredNodeRect(measureEl, portalTarget);
		} catch {
			setNodeRect(null);
		}
	}, [
		target,
		portalTarget,
		isResizing,
		updateMeasuredNodeRect,
		lineLength,
		effectiveContainerWidth,
	]);

	// The host page can move the editor center line without changing the
	// selected node or PM position, e.g. when Confluence toggles its sidebar.
	// Re-measure on observed container/layout events so handles stay attached
	// to the live DOM box even when the embed only moves horizontally.
	useLayoutEffect(() => {
		if (isResizing || !embedDomElement) {
			return;
		}

		let animationFrameId: number | null = null;
		const updateRectAndWidth = () => {
			updateMeasuredNodeRect(embedDomElement, portalTarget);

			const observedWidth = Math.round(
				getMeasuredResizeRect(embedDomElement, innerEmbedContainerRef.current).width,
			);
			if (observedWidth > 0) {
				setResizeWidth((prevWidth) => {
					return prevWidth === observedWidth ? prevWidth : observedWidth;
				});
			}
		};
		const scheduleMeasure = () => {
			if (animationFrameId !== null) {
				cancelAnimationFrame(animationFrameId);
			}
			animationFrameId = requestAnimationFrame(() => {
				animationFrameId = null;
				updateRectAndWidth();
			});
		};

		updateRectAndWidth();

		const observer =
			typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateRectAndWidth) : null;
		if (observer) {
			const elementsToObserve = new Set<HTMLElement>();
			elementsToObserve.add(embedDomElement);
			elementsToObserve.add(portalTarget);
			if (view.dom instanceof HTMLElement) {
				elementsToObserve.add(view.dom);
			}
			if (view.dom.parentElement) {
				elementsToObserve.add(view.dom.parentElement);
			}
			elementsToObserve.forEach((element) => observer.observe(element));
		}

		const ownerWindow = view.dom.ownerDocument.defaultView;
		const unbindWindowEvents = ownerWindow
			? bindAll(ownerWindow, [
					{ type: 'resize', listener: scheduleMeasure },
					{ type: 'orientationchange', listener: scheduleMeasure },
				])
			: null;
		const unbindDocumentEvents = bindAll(view.dom.ownerDocument, [
			{ type: 'transitionend', listener: scheduleMeasure },
		]);

		return () => {
			unbindWindowEvents?.();
			unbindDocumentEvents();
			observer?.disconnect();
			if (animationFrameId !== null) {
				cancelAnimationFrame(animationFrameId);
			}
		};
	}, [isResizing, embedDomElement, portalTarget, updateMeasuredNodeRect, view]);

	// During a side drag of an unlocked-AR embed, observe the inner container's
	// height so the drag handles reposition as the child iframe re-lays out.
	// Not needed for bottom drags (cursor drives height) or locked-AR embeds.
	useLayoutEffect(() => {
		if (!isResizing) {
			return;
		}
		if (bottomDraggedRef.current) {
			return;
		}
		if (aspectRatioRef.current !== null) {
			return;
		}
		const inner = innerEmbedContainerRef.current;
		if (!inner || typeof ResizeObserver === 'undefined') {
			return;
		}

		const onObserved = () => {
			const liveHeight = Math.round(inner.getBoundingClientRect().height);
			if (liveHeight <= 0 || liveHeight === fixedHeightRef.current) {
				return;
			}
			fixedHeightRef.current = liveHeight;
			setResizeHeight((prev) => (prev === liveHeight ? prev : liveHeight));
			if (embedDomElement) {
				updateMeasuredNodeRect(embedDomElement, portalTarget);
			}
		};

		const ro = new ResizeObserver(onObserved);
		ro.observe(inner);
		return () => ro.disconnect();
	}, [
		isResizing,
		activeResizeDirection,
		embedDomElement,
		portalTarget,
		updateMeasuredNodeRect,
	]);

	// -----------------------------------------------------------------------
	// Guidelines
	// -----------------------------------------------------------------------

	const defaultGuidelines = useMemo(() => {
		return isNested ? [] : generateDefaultGuidelines(lineLength, effectiveContainerWidth, false);
	}, [lineLength, effectiveContainerWidth, isNested]);

	const guidelinesRef = useMemo(() => ({ current: defaultGuidelines }), [defaultGuidelines]);

	const isGuidelineEnabled = useMemo(() => {
		return !!pluginInjectionApi?.guideline;
	}, [pluginInjectionApi]);

	// -----------------------------------------------------------------------
	// Resize handlers
	// -----------------------------------------------------------------------

	const handleWrapperMouseDown = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			const t = e.target;
			if (!(t instanceof HTMLElement)) {
				return;
			}
			const handleEl =
				t.closest('.resizer-handle') ||
				t.closest('.resizer-handle-thumb') ||
				t.closest('.resizer-handle-track') ||
				t.closest('[class*="resizer-handle"]');
			if (handleEl) {
				// Detect which handle was clicked so resizeRatio can be set per
				// direction. re-resizable applies a single resizeRatio to both
				// axes; for centered embeds we need 2 horizontally (symmetric L/R
				// drag) but 1 vertically (asymmetric bottom-only drag), otherwise
				// the bottom handle visually drifts off the embed's bottom edge.
				const handleClassName = handleEl instanceof HTMLElement ? handleEl.className : '';
				const inferredDirection: 'left' | 'right' | 'bottom' | null = (() => {
					if (
						handleClassName.includes('bottom') ||
						handleEl.closest('[data-testid*="resizer-handle-bottom"]')
					) {
						return 'bottom';
					}
					if (
						handleClassName.includes('left') ||
						handleEl.closest('[data-testid*="resizer-handle-left"]')
					) {
						return 'left';
					}
					if (
						handleClassName.includes('right') ||
						handleEl.closest('[data-testid*="resizer-handle-right"]')
					) {
						return 'right';
					}
					return null;
				})();
				setActiveResizeDirection(inferredDirection);
				// Try to claim resize ownership from the coordinator. If another
				// overlay is already mid-resize the coordinator will reject us; in
				// that case we don't flip our local resizing flag and we let the
				// event bubble normally.
				const accepted = onResizeStart ? onResizeStart(target.localId) : true;
				if (!accepted) {
					return;
				}
				isResizingRef.current = true;
				resizingStartedLocalIdRef.current = target.localId;
				e.stopPropagation();
			}
		},
		[onResizeStart, target.localId],
	);

	const handleResizeStart = useCallback(() => {
		// If we did not already claim ownership in handleWrapperMouseDown (for
		// example when ResizerNext starts a drag without our wrapper having
		// observed the mousedown), claim it here. The coordinator may reject
		// the start if another overlay is already mid-resize, in which case we
		// bail without flipping any local state or dispatching meta.
		if (resizingStartedLocalIdRef.current !== target.localId) {
			const accepted = onResizeStart ? onResizeStart(target.localId) : true;
			if (!accepted) {
				return;
			}
			resizingStartedLocalIdRef.current = target.localId;
		}

		setIsResizing(true);
		isResizingRef.current = true;
		lastSnappedGuidelineKeysRef.current = [];
		bottomDraggedRef.current = false;

		// Signal drag-start to the editor-extension so it hands width ownership
		// to the overlay for the duration of the drag (prevents width clobber).
		if (!getNativeEmbedShouldLockAspectRatio(target.node)) {
			try {
				target.container.dispatchEvent(
					new CustomEvent('native-embed-resize-state-change', {
						bubbles: true,
						detail: { active: true, localId: target.localId },
					}),
				);
			} catch {
				// best-effort signal
			}
		}

		if (!view) {
			return;
		}

		// Re-measure the actual container width from the DOM. The width
		// plugin's ResizeObserver may have reported an inflated width on
		// initial page load (e.g. before the content-tree sidebar finished
		// rendering). Using the live measurement ensures maxWidth is
		// correct for this drag session.
		try {
			const scrollParent = getDocument()?.querySelector('.fabric-editor-popup-scroll-parent');
			if (scrollParent instanceof HTMLElement) {
				const liveWidth = Math.round(scrollParent.offsetWidth);
				if (liveWidth !== containerWidth) {
					setLiveMeasuredContainerWidth(liveWidth);
				}
			}
		} catch {
			// ignore — fall back to the plugin's containerWidth
		}

		try {
			const container = target.container;
			if (container) {
				setEmbedDomElement(container);

				if (isNested) {
					const parentEl = container.offsetParent as HTMLElement | null;
					if (parentEl) {
						nestedContainerWidthRef.current = Math.round(parentEl.getBoundingClientRect().width);
					}
				} else {
					nestedContainerWidthRef.current = null;
				}

				const innerContainer =
					target.innerContainer ??
					container.querySelector<HTMLElement>('[data-testid="native-embed-extension"]') ??
					null;
				innerEmbedContainerRef.current = innerContainer;

				const rect = getMeasuredResizeRect(container, innerContainer);
				if (rect.width > 0) {
					const startWidth = Math.round(rect.width);
					setResizeWidth(startWidth);

					const shouldLockAspectRatio = getNativeEmbedShouldLockAspectRatio(target.node);
					if (shouldLockAspectRatio) {
						// Use the live DOM measurement as the aspect ratio source.
						// The stored aspectRatio parameter is not reliable — it defaults
						// to 1 when no originalWidth/originalHeight was provided, and is
						// never updated after a resize. The DOM rect at drag-start is the
						// correct height/width ratio for the embed as it is currently rendered.
						aspectRatioRef.current = rect.height / rect.width;
						fixedHeightRef.current = null;
						setResizeHeight(null);
						const effectiveHeight = Math.round(rect.width * aspectRatioRef.current);
						container.style.setProperty(
							'--native-embed-aspect-ratio',
							`${rect.width} / ${effectiveHeight}`,
						);
					} else {
						aspectRatioRef.current = null;
						const innerHeight = innerEmbedContainerRef.current
							? Math.round(innerEmbedContainerRef.current.getBoundingClientRect().height)
							: 0;
						const currentHeight =
							innerHeight > 0 ? innerHeight : Math.round(rect.height);
						fixedHeightRef.current = currentHeight;
						setResizeHeight(currentHeight);
						container.style.removeProperty('aspect-ratio');
						if (allowVerticalResize) {
							container.style.setProperty('height', `${currentHeight}px`);
							if (innerEmbedContainerRef.current) {
								innerEmbedContainerRef.current.style.removeProperty('aspect-ratio');
								innerEmbedContainerRef.current.style.setProperty('height', `${currentHeight}px`);
							}
						}
					}
				}
			}
		} catch {
			// ignore – we'll just not do live width updates
		}

		const { state, dispatch } = view;
		const tr = state.tr;
		tr.setMeta(NATIVE_EMBED_PLUGIN_IS_RESIZING_KEY, true);
		tr.setMeta('is-resizer-resizing', true);
		tr.setMeta('scrollIntoView', false);
		// NOTE: deliberately DO NOT set selection here. Selection only transfers
		// at mouseup (handleResizeStop), which keeps R4 satisfied for the hover
		// overlay (and is a no-op for the selected overlay).
		dispatch(tr);
	}, [view, target, isNested, onResizeStart, containerWidth, allowVerticalResize]);

	const scheduleResizeFrame = useCallback(() => {
		if (!view) {
			return;
		}
		if (resizeFrameRafRef.current !== null) {
			return;
		}
		resizeFrameRafRef.current = requestAnimationFrame(() => {
			resizeFrameRafRef.current = null;
			if (!isResizingRef.current || !view || view.isDestroyed) {
				return;
			}
			const tr = view.state.tr;
			tr.setMeta('native-embed-resize-frame', true);
			tr.setMeta('addToHistory', false);
			tr.setMeta('scrollIntoView', false);
			view.dispatch(tr);
		});
	}, [view]);

	const handleResize: HandleResize = useCallback(
		(size: Position & Dimensions, delta: Dimensions, direction) => {
			if (direction === 'bottom' && allowVerticalResize) {
				// resizeRatio is forced to 1 for vertical drags below, so
				// delta.height already reflects the cursor movement 1:1 — no
				// need to divide. This keeps the bottom handle stuck to the
				// embed's bottom edge for centered embeds (where horizontal
				// resizeRatio is 2 to support symmetric L/R drag).
				const clampedHeight = Math.max(MIN_HEIGHT, Math.round(size.height + delta.height));
				fixedHeightRef.current = clampedHeight;
				bottomDraggedRef.current = true;
				setResizeHeight(clampedHeight);
				scheduleResizeFrame();
				return;
			}

			const newWidth = Math.round(size.width + delta.width);
			const effectiveMax =
				isNested && nestedContainerWidthRef.current !== null
					? Math.min(maxWidth, nestedContainerWidthRef.current)
					: maxWidth;

			const clampedWidth = Math.max(MIN_WIDTH, Math.min(newWidth, effectiveMax));

			setResizeWidth(clampedWidth);

			const isSideAligned =
				currentAlignment === 'left' ||
				currentAlignment === 'right' ||
				currentAlignment === 'wrap-left' ||
				currentAlignment === 'wrap-right';
			if (!isNested && isSideAligned && clampedWidth > lineLength) {
				onAlignmentChange?.('center');
				if (view?.state && view?.dispatch) {
					// Re-resolve the live node by localId so we operate on the
					// current PMNode instance even if a transaction has landed
					// during the drag and shifted positions.
					const resolved = resolveLatestPosByLocalId(view, target.localId);
					const liveNode = resolved?.node ?? target.node;
					if (liveNode) {
						createUpdateAlignmentCommand(
							pluginInjectionApi,
							'center',
							liveNode,
						)(view?.state, view?.dispatch);
					}
				}
			}

			if (isGuidelineEnabled) {
				const layoutForGuidelines = mapAlignmentToLayout(currentAlignment);
				const guidelineSnaps = getGuidelineSnaps(
					guidelinesRef.current,
					lineLength,
					layoutForGuidelines,
				);

				if (guidelineSnaps.snaps.x) {
					const { gap, keys: activeGuidelineKeys } = findClosestSnap(
						clampedWidth,
						guidelineSnaps.snaps.x,
						guidelineSnaps.guidelineReference,
						SNAP_GAP,
					);

					lastSnappedGuidelineKeysRef.current = activeGuidelineKeys;

					const nextGuideLines = getGuidelinesWithHighlights(
						gap,
						SNAP_GAP,
						activeGuidelineKeys,
						guidelinesRef.current,
					);
					view &&
						pluginInjectionApi?.guideline?.actions?.displayGuideline(view)({
							guidelines: nextGuideLines,
						});
				}

				setSnaps({
					x: guidelineSnaps.snaps.x || [],
				});
			}
		},
		[
			maxWidth,
			isNested,
			lineLength,
			guidelinesRef,
			isGuidelineEnabled,
			currentAlignment,
			target,
			pluginInjectionApi,
			view,
			onAlignmentChange,
			allowVerticalResize,
			scheduleResizeFrame,
		],
	);

	useLayoutEffect(() => {
		if (!isResizing) {
			return;
		}
		if (embedDomElement) {
			embedDomElement.style.transition = 'none';
			if (isNested) {
				embedDomElement.style.width = '';
				embedDomElement.style.minWidth = '';
				embedDomElement.style.maxWidth = `${resizeWidth}px`;
				embedDomElement.style.setProperty(CSS_VAR_NATIVE_EMBED_WIDTH, `${resizeWidth}px`);
			} else {
				// Write `--native-embed-width` FIRST — it's the cascade winner for
				// `.extension-container` width. Removing it even briefly causes
				// `width: var(--native-embed-width) !important` to resolve to
				// `auto` (full editor column width), producing a visible snap.
				embedDomElement.style.setProperty(CSS_VAR_NATIVE_EMBED_WIDTH, `${resizeWidth}px`);
				embedDomElement.style.width = `${resizeWidth}px`;
				embedDomElement.style.minWidth = `${resizeWidth}px`;
				embedDomElement.style.maxWidth = `${resizeWidth}px`;
			}
			if (resizeHeight !== null) {
				// Only force height for bottom drags; side drags let the child's
				// local height overrides flow through naturally.
				if (bottomDraggedRef.current) {
					embedDomElement.style.height = `${resizeHeight}px`;
					embedDomElement.style.removeProperty('aspect-ratio');
				}
			}
		}
		const innerContainer = innerEmbedContainerRef.current;
		if (innerContainer) {
			innerContainer.style.width = `${resizeWidth}px`;
			if (resizeHeight !== null && bottomDraggedRef.current) {
				innerContainer.style.height = `${resizeHeight}px`;
				innerContainer.style.removeProperty('aspect-ratio');
			}
		}
	}, [isResizing, resizeWidth, resizeHeight, embedDomElement, isNested, activeResizeDirection]);

	const handleResizeStop: HandleResize = useCallback(
		(_size: Position & Dimensions, _delta: Dimensions) => {
			if (!getNativeEmbedShouldLockAspectRatio(target.node)) {
				try {
					target.container.dispatchEvent(
						new CustomEvent('native-embed-resize-state-change', {
							bubbles: true,
							detail: { active: false, localId: target.localId },
						}),
					);
				} catch {
					// best-effort signal
				}
			}

			const isFullWidthSnap = lastSnappedGuidelineKeysRef.current.some((key) =>
				key.startsWith('full_width'),
			);
			const committedWidth =
				!isNested && isFullWidthSnap ? akEditorFullWidthLayoutWidth : resizeWidth;

			committedWidthRef.current = committedWidth;

			if (
				allowVerticalResize &&
				expValEquals('databases-native-embeds-v2', 'isEnabled', true) &&
				bottomDraggedRef.current &&
				aspectRatioRef.current === null &&
				fixedHeightRef.current !== null
			) {
				const liveBoundaries =
					liveRowBoundariesRef.current ?? getRowBoundaries(target.localId);
				if (liveBoundaries && liveBoundaries.length > 0) {
					const rawHeight = fixedHeightRef.current;
					const snappedHeight = snapHeightToRowBoundary(rawHeight, liveBoundaries, {
						minHeight: MIN_HEIGHT,
					});

					if (snappedHeight !== rawHeight) {
						fixedHeightRef.current = snappedHeight;
					}
				}
			}

			// fixedHeightRef was captured at drag-start; re-measure the live
			// height before clearing styles so we commit the correct value.
			const innerContainer = innerEmbedContainerRef.current;
			if (
				fixedHeightRef.current !== null &&
				innerContainer &&
				!bottomDraggedRef.current
			) {
				try {
					const liveHeight = Math.round(innerContainer.getBoundingClientRect().height);
					if (liveHeight > 0) {
						fixedHeightRef.current = liveHeight;
					}
				} catch {
					// Best-effort — keep the drag-start value.
				}
			}

			if (embedDomElement) {
				embedDomElement.style.transition = '';
				// Write `--native-embed-width` before clearing inline overrides —
				// same reason as the drag-time effect above.
				embedDomElement.style.setProperty(CSS_VAR_NATIVE_EMBED_WIDTH, `${committedWidth}px`);
				embedDomElement.style.width = '';
				embedDomElement.style.minWidth = '';
				embedDomElement.style.setProperty('max-width', 'var(--ak-editor-max-container-width)');
				if (fixedHeightRef.current !== null) {
					embedDomElement.style.setProperty('height', `${fixedHeightRef.current}px`);
					embedDomElement.style.removeProperty('aspect-ratio');
				} else {
					embedDomElement.style.setProperty('height', 'auto');
				}
			}

			if (innerContainer) {
				innerContainer.style.width = '';
				if (fixedHeightRef.current !== null) {
					innerContainer.style.height = `${fixedHeightRef.current}px`;
					innerContainer.style.removeProperty('aspect-ratio');
				} else {
					innerContainer.style.height = '';
				}
			}
			innerEmbedContainerRef.current = null;

			view && pluginInjectionApi?.guideline?.actions?.displayGuideline(view)({ guidelines: [] });
			lastSnappedGuidelineKeysRef.current = [];

			// Helper: release this overlay's claim on the coordinator's resize
			// ownership. We pair start/stop by localId so the coordinator can
			// safely ignore stops for any embed other than the one currently
			// owning the resize (defends against unmount-cleanup races).
			const releaseResizeOwnership = () => {
				const startedLocalId = resizingStartedLocalIdRef.current;
				resizingStartedLocalIdRef.current = null;
				if (startedLocalId !== null) {
					onResizeStop?.(startedLocalId);
				}
			};

			if (!view) {
				setIsResizing(false);
				setResizeHeight(null);
				isResizingRef.current = false;
				setActiveResizeDirection(null);
				releaseResizeOwnership();
				return;
			}

			// Re-resolve the live node + position via localId so we operate on
			// the current PMNode instance even if a transaction has landed
			// during the drag and shifted positions. Cached `target.pos` is
			// only used as a fallback for the selection transfer below.
			const resolved = resolveLatestPosByLocalId(view, target.localId);
			const liveNode = resolved?.node ?? target.node;
			if (!liveNode) {
				setIsResizing(false);
				setResizeHeight(null);
				isResizingRef.current = false;
				setActiveResizeDirection(null);
				releaseResizeOwnership();
				return;
			}

			const committedHeight =
				aspectRatioRef.current !== null
					? Math.round(committedWidth * aspectRatioRef.current)
					: (fixedHeightRef.current ?? undefined);
			if (committedHeight !== undefined) {
				committedHeightRef.current = committedHeight;
			} else {
				committedHeightRef.current = null;
			}
			// Pass `scrollIntoView: false` through to the extension `doc.update`
			// transaction so the width-commit tx never triggers PM's
			// auto-scroll-to-selection behaviour. Without this opt the
			// underlying `tr.replaceWith(...)` lands while PM selection is
			// still on a previously-selected embed (e.g. one further up the
			// page), and PM scrolls that stale selection back into view on
			// mouseup.
			createUpdateWidthCommand(pluginInjectionApi, committedWidth, liveNode, committedHeight, {
				scrollIntoView: false,
			})(view.state, view.dispatch);

			// -----------------------------------------------------------------
			// Selection transfer: only at mouseup. With the continuous-overlay
			// architecture the coordinator renders one EmbedResizeOverlay per
			// embed (keyed by localId, with `mode` as a prop), so transferring
			// selection here does NOT cause the overlay to remount — it only
			// flips this overlay's mode prop. This means we can dispatch the
			// selection transfer synchronously without a flicker.
			//
			// Skip the dispatch if the selection is already on this node so we
			// don't add a spurious history entry.
			// -----------------------------------------------------------------
			try {
				const { state: selState, dispatch: selDispatch } = view;
				const currentSelection = selState.selection;
				const resolvedSelectionPos = resolved?.pos ?? target.pos;

				const isAlreadySelected =
					currentSelection instanceof NodeSelection &&
					currentSelection.from === resolvedSelectionPos;

				if (!isAlreadySelected) {
					const selectionTr = selState.tr;
					selectionTr.setSelection(NodeSelection.create(selState.doc, resolvedSelectionPos));
					selectionTr.setMeta('scrollIntoView', false);
					selDispatch(selectionTr);
				}
			} catch {
				// ignore – selection transfer is best-effort
			}

			const { state, dispatch } = view;
			const tr = state.tr;
			tr.setMeta(NATIVE_EMBED_PLUGIN_IS_RESIZING_KEY, false);
			tr.setMeta('is-resizer-resizing', false);
			tr.setMeta('scrollIntoView', false);
			dispatch(tr);

			requestAnimationFrame(() => {
				setIsResizing(false);
				setResizeHeight(null);
				isResizingRef.current = false;
				setActiveResizeDirection(null);
				releaseResizeOwnership();
			});
			aspectRatioRef.current = null;
			fixedHeightRef.current = null;

			if (resizeFrameRafRef.current !== null) {
				cancelAnimationFrame(resizeFrameRafRef.current);
				resizeFrameRafRef.current = null;
			}
			bottomDraggedRef.current = false;
		},
		[
			target,
			pluginInjectionApi,
			view,
			embedDomElement,
			resizeWidth,
			isNested,
			onResizeStop,
			allowVerticalResize,
		],
	);

	// -----------------------------------------------------------------------
	// Early return — nothing to render
	// -----------------------------------------------------------------------

	// The coordinator gates mounting:
	// - selected mode: overlay is mounted whenever an embed is selected.
	// - hover mode:    overlay is mounted only while the coordinator says the
	//                  embed is hovered (so we should be visible immediately).
	// We always render once we have a measured rect.
	if (!nodeRect) {
		return null;
	}

	const isRightTypeAligned = currentAlignment === 'right' || currentAlignment === 'wrap-right';
	const isLeftTypeAligned = currentAlignment === 'left' || currentAlignment === 'wrap-left';

	const enableRightHandle =
		!handlesBlocked && currentAlignment !== 'right' && currentAlignment !== 'wrap-right';
	const enableLeftHandle =
		!handlesBlocked && currentAlignment !== 'left' && currentAlignment !== 'wrap-left';
	const enableBottomHandle = !handlesBlocked && allowVerticalResize;

	let wrapperLeft: number;
	const actualNestedMax = nestedContainerWidthRef.current;
	const overlayWidth = isResizing
		? isNested && actualNestedMax !== null
			? Math.min(resizeWidth, actualNestedMax)
			: resizeWidth
		: Math.round(nodeRect.width);
	const overlayHeight =
		isResizing && resizeHeight !== null ? resizeHeight : Math.round(nodeRect.height);

	if (isResizing && embedDomElement && (isLeftTypeAligned || isRightTypeAligned)) {
		const portalRect = portalTarget.getBoundingClientRect();
		const embedRect = embedDomElement.getBoundingClientRect();
		wrapperLeft = embedRect.left - portalRect.left + portalTarget.scrollLeft;
	} else if (isResizing && embedDomElement) {
		const portalRect = portalTarget.getBoundingClientRect();
		const embedRect = embedDomElement.getBoundingClientRect();
		if (isNested) {
			wrapperLeft = embedRect.left - portalRect.left + portalTarget.scrollLeft;
		} else {
			const centerX =
				embedRect.left - portalRect.left + portalTarget.scrollLeft + embedRect.width / 2;
			wrapperLeft = centerX - overlayWidth / 2;
		}
	} else if (isRightTypeAligned || isLeftTypeAligned) {
		wrapperLeft = nodeRect.left;
	} else {
		const centerX = nodeRect.left + nodeRect.width / 2;
		wrapperLeft = centerX - overlayWidth / 2;
	}

	const resizerWrapperDynamicStyles = {
		top: nodeRect.top,
		left: wrapperLeft,
		width: overlayWidth,
	};

	// Visibility rules:
	// - selected overlay: handles always visible (R1) — independent of hover
	// - hover overlay: only mounted by the coordinator while the embed is
	//   hovered, so handles should be visible whenever it is mounted (R2/R3)
	const isHandleVisible = !handlesBlocked && !!nodeRect.height;

	const resizerMaxWidthBase = isNested && actualNestedMax !== null ? actualNestedMax : maxWidth;
	// While idle, the hover overlay is only drawing handles. If the layout-column
	// max is narrower than the measured frame, re-resizable visually clamps the
	// rail inward over the embed scrollbar, so keep display max at the measured
	// width. Active drags still use the real nested clamp.
	const resizerMaxWidth =
		mode === 'hover' && !isResizing
			? Math.max(resizerMaxWidthBase, overlayWidth)
			: resizerMaxWidthBase;
	// re-resizable applies a single resizeRatio to both axes. For centered
	// embeds the horizontal handles need ratio 2 (one drag = symmetric L/R
	// expansion), but the bottom handle is asymmetric (only the bottom edge
	// moves) and must use ratio 1 so it stays glued to the embed's bottom.
	const resizeRatio =
		activeResizeDirection === 'bottom' ? 1 : currentAlignment === 'center' ? 2 : 1;

	/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop -- dynamic overlay positioning from DOM measurements */
	const overlay = (
		<div
			data-testid="native-embed-resizer"
			data-overlay-mode={mode}
			data-embed-local-id={target.localId}
			role="presentation"
			onMouseDown={handleWrapperMouseDown}
		>
			<div
				css={[resizerWrapperStyles]}
				style={resizerWrapperDynamicStyles}
				data-testid="native-embed-resizer-wrapper"
			>
				<ResizerNext
					/* eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) */
					enable={{
						left: enableLeftHandle,
						right: enableRightHandle,
						bottom: enableBottomHandle,
					}}
					width={overlayWidth}
					height={allowVerticalResize ? overlayHeight : undefined}
					minWidth={MIN_WIDTH}
					maxWidth={resizerMaxWidth}
					minHeight={allowVerticalResize ? MIN_HEIGHT : undefined}
					handleResizeStart={handleResizeStart}
					handleResize={handleResize}
					handleResizeStop={handleResizeStop}
					snapGap={SNAP_GAP}
					snap={snaps}
					resizeRatio={resizeRatio}
					isHandleVisible={!!isHandleVisible}
					handleHighlight="full-height"
					handlePositioning="adjacent"
					handleStyles={bottomHandleStyles}
					data-testid={resizerNextTestId}
				>
					{/* Compute placeholder height outside the style prop to avoid the
					   @atlaskit/ui-styling-standard/no-imported-style-values lint rule
					   which flags imported function calls inside style attributes. */}
					{(() => {
						const placeholderHeight = (() => {
							if (isResizing && resizeHeight !== null) {
								return resizeHeight;
							}
							if (isResizing && aspectRatioRef.current !== null) {
								return Math.round(resizeWidth * aspectRatioRef.current);
							}
							// When not resizing, prefer the live DOM height — it reflects
							// what the embed is actually rendering at. Only fall back to
							// an aspectRatio calculation when the DOM height is unavailable.
							if (nodeRect.height) {
								return nodeRect.height;
							}
							const storedAspectRatio = getParameters(
								target.node.attrs.parameters,
								'aspectRatio',
							) as number | undefined;
							if (storedAspectRatio != null && storedAspectRatio > 0) {
								// storedAspectRatio is width/height; height = width / aspectRatio.
								// Use overlayWidth (actual DOM width) so the placeholder matches
								// the visible embed proportions.
								return Math.round(overlayWidth / storedAspectRatio);
							}
							return 600; // TODO: CNS-24595 - 600 is a hack, it can lose the select in cases and just falls back to this. We can do better
						})();
						return (
							<div
								css={resizerPlaceholderStyles}
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- dynamic height from DOM
								style={{ height: placeholderHeight }}
							/>
						);
					})()}
				</ResizerNext>
			</div>
		</div>
	);
	/* eslint-enable @atlaskit/ui-styling-standard/enforce-style-prop */

	return ReactDOM.createPortal(overlay, portalTarget);
};
